/**
 * GoalsCardBody — the content of the Dashboard's compact Goals card.
 *
 * Renders the honest goals rollup (`goalsSummary` over the live
 * orchestrator runs): distinct active goals, runs in flight, and the next
 * milestone a run is waiting on — or the honest zero state ("No goals yet
 * — set one") when nothing has ever run. The card itself (DashboardView's
 * PulseCard) is a full-card button that navigates to the Goals view, so
 * this body deliberately renders ONLY spans (no nested interactive or
 * block elements).
 */

import { goalsSummary } from '../lib/crm/dashboardPulse';
import { C } from '../lib/tokens';
import type { OrchestratorRun } from '../lib/db/types';

export function GoalsCardBody({ runs }: { runs: OrchestratorRun[] }) {
  const s = goalsSummary(runs);

  if (s.totalRuns === 0) {
    // Honest zero state — the surrounding card click opens the Goals view.
    return (
      <span style={{ display: 'grid', gap: 6 }}>
        <span className="t-card-title">No goals yet — set one.</span>
        <span className="t-meta" style={{ color: C.textMuted }}>
          Open Goals to launch a governed, step-by-step run.
        </span>
      </span>
    );
  }

  return (
    <span style={{ display: 'grid', gap: 6 }}>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span className="t-page-title" style={{ fontSize: 26, lineHeight: 1 }}>
          {s.activeGoals}
        </span>
        <span className="t-meta" style={{ color: C.textMuted }}>
          active {s.activeGoals === 1 ? 'goal' : 'goals'} · {s.activeRuns}{' '}
          {s.activeRuns === 1 ? 'run' : 'runs'} in flight
        </span>
      </span>
      <span className="t-meta" style={{ color: C.textMuted }}>
        {s.nextMilestone
          ? `Next: ${s.nextMilestone}`
          : s.activeRuns > 0
            ? 'Every queued step is resolved — runs are wrapping up.'
            : `Nothing in flight — ${s.totalRuns} past ${s.totalRuns === 1 ? 'run' : 'runs'} on record.`}
      </span>
    </span>
  );
}
